import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { Text } from '@change/ds-core-ui/Text';

import { Translate } from '@change-corgi/core-react/i18n';

import type { Endorsement } from '#src/app/shared/hooks/endorsements';

import { EndorsementCardEdit } from '../../../EndorsementCardEdit';
import {
	endorsementActionsOuter,
	endorsementActionsRow,
	endorsementCardContainer,
	endorsementLimitMessage,
} from '../../EndorsementSection.styles';
import { useEndorsementSectionWithList } from '../../hooks/useEndorsementSectionWithList';
import { DeleteEndorsementModal } from '../DeleteEndorsementModal';

const getEndorsementDescription = (content: string | null | undefined, isContentExpanded: boolean): string => {
	const fullContent = content ?? '';
	const hasLongContent = fullContent.length > 200;
	const shouldTruncate = hasLongContent && !isContentExpanded;

	return shouldTruncate ? `${fullContent.slice(0, 200)}...` : fullContent;
};

const EndorsementItem: React.FC<
	Readonly<{
		endorsement: Endorsement;
		isContentExpanded: boolean;
		onToggleExpansion: () => void;
		onEdit: (id: string) => void;
		onRemove: (id: string) => void;
	}>
> = ({ endorsement, isContentExpanded, onToggleExpansion, onEdit, onRemove }) => {
	const contentLength = endorsement.content?.length ?? 0;
	const shouldShowReadMore = contentLength > 200;
	const displayedDescription = getEndorsementDescription(endorsement.content, isContentExpanded);
	const readMoreButtonLabel = isContentExpanded ? 'View less' : 'Read full endorsement';

	return (
		<EndorsementCardEdit
			name={endorsement.name}
			title={endorsement.title ?? ''}
			description={displayedDescription}
			photoUrl={endorsement.photo?.userSmall.url ?? ''}
			link={endorsement.url ?? '#'}
			onEdit={() => onEdit(endorsement.id)}
			onRemove={() => onRemove(endorsement.id)}
			onReadMore={shouldShowReadMore ? onToggleExpansion : undefined}
			readMoreLabel={shouldShowReadMore ? readMoreButtonLabel : undefined}
			className="bg-background-base border-stroke-weak rounded-md border"
		/>
	);
};

const EndorsementList: React.FC<
	Readonly<{
		endorsements: readonly Endorsement[];
		isContentExpanded: boolean;
		onToggleExpansion: () => void;
		onEdit: (id: string) => void;
		onRemove: (id: string) => void;
	}>
> = ({ endorsements, isContentExpanded, onToggleExpansion, onEdit, onRemove }) => {
	return (
		<div className={endorsementCardContainer()}>
			{endorsements.map((endorsement) => (
				<EndorsementItem
					key={endorsement.id}
					endorsement={endorsement}
					isContentExpanded={isContentExpanded}
					onToggleExpansion={onToggleExpansion}
					onEdit={onEdit}
					onRemove={onRemove}
				/>
			))}
		</div>
	);
};

export const EndorsementSectionWithList: React.FC<EmptyProps> = () => {
	const {
		endorsements,
		isAtEndorsementLimit,
		isContentExpanded,
		isDeleteModalOpen,
		isDeleting,
		setIsDeleteModalOpen,
		handleAddAnother,
		handleViewOnPetition,
		handleEdit,
		handleRemove,
		handleToggleContentExpansion,
		handleConfirmDelete,
	} = useEndorsementSectionWithList();

	return (
		<>
			<EndorsementList
				endorsements={endorsements}
				isContentExpanded={isContentExpanded}
				onToggleExpansion={handleToggleContentExpansion}
				onEdit={handleEdit}
				onRemove={handleRemove}
			/>

			<div className={endorsementActionsOuter()}>
				<div className={endorsementActionsRow()}>
					<Button
						disabled={isAtEndorsementLimit}
						onClick={handleAddAnother}
						variant="secondary"
						size="x-small"
						data-testid="add-another-endorsement-button"
					>
						<Translate value="corgi.petition.edit.endorsements.add_another_button" />
					</Button>
					<Button
						onClick={handleViewOnPetition}
						variant="tertiary"
						size="x-small"
						data-testid="view-on-petition-button"
					>
						<Translate value="corgi.petition.edit.endorsements.view_on_petition_button" />
					</Button>
				</div>
				{isAtEndorsementLimit && (
					<Text
						as="p"
						variant="body"
						size="small"
						className={endorsementLimitMessage()}
						data-testid="endorsements-limit-reached-message"
					>
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.limit_reached_message" />
					</Text>
				)}
			</div>

			<DeleteEndorsementModal
				isOpen={isDeleteModalOpen}
				onOpenChange={setIsDeleteModalOpen}
				onConfirmDelete={handleConfirmDelete}
				isDeleting={isDeleting}
			/>
		</>
	);
};
